import React from 'react'
import BookkeepingSubmitAssessmentDialogViewModel from './BookkeepingSubmitAssessmentDialogViewModel'
import { observer } from 'mobx-react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetTitle,
} from 'components/ModalSheet'
import {
  Form,
  FormRow,
  NumericInputContainer,
  SwitchCheckboxInputContainer,
  TextAreaContainer,
} from 'components/inputs'
import Button from 'components/Button'

interface BookkeepingSubmitAssessmentDialogProps {
  vm: BookkeepingSubmitAssessmentDialogViewModel
}

export default observer(function BookkeepingSubmitAssessmentDialog({
  vm,
}: BookkeepingSubmitAssessmentDialogProps) {
  return (
    <ModalSheet
      showing={vm.showing}
      onRequestClose={vm.submit.pending ? undefined : vm.close}
    >
      <ModalSheetHeader bordered withCloseButton>
        <ModalSheetTitle>Bookkeeping assessment</ModalSheetTitle>
      </ModalSheetHeader>
      <ModalSheetContent>
        <ModalSheetContentBlock expanded>
          <Form>
            <FormRow>
              <NumericInputContainer
                model={vm}
                precision={0}
                min={vm.fieldLengths.numberOfBankAccounts.min}
                max={vm.fieldLengths.numberOfBankAccounts.max}
                field="numberOfBankAccounts"
                name="numberOfBankAccounts"
                label="Number of bank accounts"
                data-test="submit-assessment-number-of-bank-accounts"
                error={vm.validation.getError('numberOfBankAccounts')}
              />
            </FormRow>
            <FormRow>
              <NumericInputContainer
                model={vm}
                precision={2}
                min={vm.fieldLengths.avgMonthlyExpenses.min}
                max={vm.fieldLengths.avgMonthlyExpenses.max}
                field="avgMonthlyExpenses"
                name="avgMonthlyExpenses"
                currencyCode="usd"
                label="Last 3 month average monthly expenses"
                data-test="submit-assessment-avg-monthly-expenses"
                error={vm.validation.getError('avgMonthlyExpenses')}
              />
            </FormRow>
            <FormRow>
              <SwitchCheckboxInputContainer
                model={vm}
                noText
                field="cleanup"
                label="Cleanup required?"
                help="Customer requires a cleanup"
                data-test="submit-assessment-cleanup"
              />
            </FormRow>
            {vm.cleanup && (
              <FormRow>
                <TextAreaContainer
                  rows={4}
                  model={vm}
                  field="cleanupReason"
                  name="cleanupReason"
                  label="Description"
                  placeholder="Cleanup reason"
                  maxLength={vm.fieldLengths.cleanupReason.max}
                  error={vm.validation.getError('cleanupReason')}
                  status={
                    vm.validation.getError('cleanupReason')
                      ? 'danger'
                      : 'default'
                  }
                  data-test="submit-assessment-cleanup-reason"
                />
              </FormRow>
            )}
            <FormRow>
              <TextAreaContainer
                rows={4}
                model={vm}
                field="comment"
                name="comment"
                label="Comments"
                placeholder="Assessment comments"
                maxLength={vm.fieldLengths.comment.max}
                error={vm.validation.getError('comment')}
                status={
                  vm.validation.getError('comment') ? 'danger' : 'default'
                }
                data-test="submit-assessment-comment"
              />
            </FormRow>
          </Form>
        </ModalSheetContentBlock>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            type="default"
            onClick={vm.close}
            disabled={vm.submit.pending}
            data-test="submit-assessment-close-button"
          >
            Close
          </Button>
          <Button
            type="primary"
            data-test="submit-assessment-submit-button"
            onClick={vm.submit}
            loading={vm.submit.pending}
            disabled={vm.submit.pending}
          >
            Submit
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})
